import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { ORDER_PAYMENT_TERMS_LABELS_RU, isCrmCoreRole, isOrderEditable } from '@crm/shared'
import { getOrder, deleteOrder } from '../../shared/api/orders.api'
import { confirm } from '../../shared/ui/confirmStore'
import { toast } from '../../shared/ui/toastStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useAuthStore } from '../auth/authStore'
import { OrderStatusBadge } from './components/OrderStatusBadge'
import { OrderPositionsSection } from './components/OrderPositionsSection'
import { InvoicesSection } from './components/InvoicesSection'
import { PaymentsSection } from './components/PaymentsSection'
import { DocumentsSection } from './components/DocumentsSection'
import { OrderHistorySection } from './components/OrderHistorySection'
import { ApproveOrderModal } from './components/ApproveOrderModal'
import { OrderStatusActions } from './components/OrderStatusActions'
import { CreateOrderButton } from './components/CreateOrderButton'
import { PaymentTermsModal } from './components/PaymentTermsModal'
import { UnfInvoiceButton } from './unf-invoice/UnfInvoiceButton'
import { OrderInvoiceCheck } from './unf-invoice/OrderInvoiceCheck'
import { formatMoney, round2 } from './orderFormat'

export function OrderDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const [editTerms, setEditTerms] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)

  const query = useQuery({
    queryKey: ['orders', id],
    queryFn: () => getOrder(id),
    retry: (count, err) => {
      const status = (err as AxiosError).response?.status
      if (status === 403 || status === 404) return false
      return count < 2
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteOrder(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] })
      toast.success('Заказ удалён')
      void navigate('/orders')
    },
    onError: (e) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err.response?.data?.error?.message ?? 'Не удалось удалить заказ')
    },
  })

  if (query.isLoading) return <PageLoader label="Загружаю заказ…" />
  if (query.isError) {
    const status = (query.error as AxiosError).response?.status
    if (status === 403 || status === 404) {
      return (
        <EmptyState
          title={status === 403 ? 'Нет доступа к этому заказу' : 'Заказ не найден'}
          action={
            <Link to="/orders" className="text-sm text-blue-600 hover:underline">
              ← К списку заказов
            </Link>
          }
        />
      )
    }
    return <ErrorMessage error={query.error} />
  }

  const order = query.data!
  // Источник истины по деньгам — сумма выставленных счетов (invoicedSum), не КП.
  const remaining = order.remainingSum
  // Разницу меньше рубля не показываем: счёт «НДС сверху» считается от цены без НДС,
  // а сумма «по КП» — от цены с НДС, и копейка расхождения сбивала бухгалтера.
  const kpMismatch = Math.abs(round2(order.invoicedSum) - round2(order.totalSum)) >= 1
  const canApprove = (role === 'ADMIN' || role === 'ROP') && order.status === 'PENDING_ROP'
  // Б7: админ может переопределить отказ (в т.ч. решение РОПа) — вернуть заказ в работу.
  const canOverrideCancel = role === 'ADMIN' && order.status === 'CANCELLED'
  const canMutate = role === 'ADMIN' || role === 'MANAGER' || role === 'ROP' || role === 'ASSISTANT'
  // Бухгалтер (этап 3, Б1) заказ только смотрит: карточка заявки ему закрыта.
  const canOpenRequest = isCrmCoreRole(role ?? '')
  // Состав заказа меняется до полной оплаты (29.09.2026, docs/features/order-editing.md).
  const canEdit = canMutate && isOrderEditable(order.status)

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Удалить заказ по заявке ${order.requestNumber ?? order.requestId.slice(0, 8)}?`,
      message:
        (order.paidSum > 0
          ? `По заказу зафиксированы оплаты на ${formatMoney(order.paidSum)}. `
          : '') +
        'Счета, платежи, документы и история заказа будут удалены. Заявка и её статусы не изменятся. Действие необратимо.',
      danger: true,
      confirmText: 'Удалить заказ',
    })
    if (!ok) return
    deleteMutation.mutate()
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/orders" className="text-sm text-blue-600 hover:underline">
            ← К списку заказов
          </Link>
          <h1 className="mt-1 flex items-center gap-3 text-2xl font-semibold">
            Заказ по заявке{' '}
            {canOpenRequest ? (
              <Link to={`/requests/${order.requestId}`} className="text-blue-600 hover:underline">
                {order.requestNumber ?? order.requestId.slice(0, 8)}
              </Link>
            ) : (
              <span>{order.requestNumber ?? order.requestId.slice(0, 8)}</span>
            )}
            <OrderStatusBadge status={order.status} />
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {order.clientName} · менеджер: {order.managerName ?? '—'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canApprove && (
            <button
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600"
              onClick={() => setApproveOpen(true)}
            >
              Согласовать
            </button>
          )}
          {canOverrideCancel && (
            <button
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600"
              onClick={() => setApproveOpen(true)}
            >
              Вернуть в работу
            </button>
          )}
          <UnfInvoiceButton requestId={order.requestId} />
          {canMutate && (
            <CreateOrderButton
              requestId={order.requestId}
              // Сводный шаблон УНФ требует одного юрлица у всех счетов заказа.
              defaultEntityId={
                order.invoices.find((i) => i.status !== 'CANCELLED' && i.entityId)?.entityId
              }
              label="Выгрузить ещё счёт (УНФ)"
              forExistingOrder
            />
          )}
          {canMutate && (
            <button
              className="rounded-lg bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200 disabled:opacity-60"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Удаляю…' : 'Удалить заказ'}
            </button>
          )}
        </div>
      </div>

      <OrderInvoiceCheck order={order} canMutate={canMutate} />

      {/* Сводка */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Сумма по счетам"
          value={formatMoney(order.invoicedSum)}
          sub={kpMismatch ? `по КП: ${formatMoney(order.totalSum)}` : undefined}
        />
        <Stat label="Оплачено" value={formatMoney(order.paidSum)} accent="text-green-700" />
        <Stat label="Осталось" value={formatMoney(remaining)} />
        <Stat
          label="Условие оплаты"
          value={`${ORDER_PAYMENT_TERMS_LABELS_RU[order.paymentTermsType]} (${order.prepaymentPercent}%)`}
          action={
            canMutate && isOrderEditable(order.status) ? (
              <button
                type="button"
                onClick={() => setEditTerms(true)}
                className="text-xs text-brand-600 hover:text-brand-700"
              >
                Изменить
              </button>
            ) : undefined
          }
        />
      </div>
      {editTerms && <PaymentTermsModal order={order} onClose={() => setEditTerms(false)} />}

      {order.prepaymentPercent > 0 &&
        (() => {
          const planned = round2((order.invoicedSum * order.prepaymentPercent) / 100)
          const left = Math.max(0, round2(planned - order.paidSum))
          const done = order.paidSum >= planned
          return (
            <div
              className={`mb-4 rounded-xl px-3 py-2 text-sm ${done ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`}
            >
              Плановая предоплата ({order.prepaymentPercent}%): {formatMoney(planned)} · оплачено{' '}
              {formatMoney(order.paidSum)} ·{' '}
              {done ? 'предоплата получена ✓' : `до предоплаты осталось ${formatMoney(left)}`}
            </div>
          )
        })()}

      {(order.paymentTermsType === 'DEFERRED' || order.paymentTermsType === 'POSTPAYMENT') &&
        order.deferralDays != null && (
          <div className="mb-4 rounded-xl bg-blue-50 px-3 py-2 text-sm text-blue-800">
            {order.paymentTermsType === 'DEFERRED' ? 'Отсрочка' : 'Постоплата'}:{' '}
            {order.deferralDays} дн. — ждём оплату {formatMoney(order.invoicedSum)} (оплачено{' '}
            {formatMoney(order.paidSum)}).
          </div>
        )}

      {order.status === 'PENDING_ROP' && (
        <div className="mb-4 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Заказ ждёт согласования РОП (плановая предоплата меньше 50%). Шаблон счёта уже доступен.
        </div>
      )}
      {order.cancelReason && (
        <div className="mb-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">
          Причина отказа: {order.cancelReason}
        </div>
      )}

      {canMutate && <OrderStatusActions order={order} />}

      <div className="mt-5 space-y-6">
        <OrderPositionsSection order={order} canMutate={canMutate} canEdit={canEdit} />
        <InvoicesSection order={order} canMutate={canMutate} />
        <PaymentsSection order={order} canMutate={canMutate} />
        <DocumentsSection order={order} canMutate={canMutate} />
        <OrderHistorySection order={order} />
        {canOpenRequest && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600">
            Комментарии и история переписки ведутся в{' '}
            <Link to={`/requests/${order.requestId}`} className="text-blue-600 hover:underline">
              карточке заявки
            </Link>
            .
          </div>
        )}
      </div>

      {approveOpen && (
        <ApproveOrderModal
          orderId={order.id}
          overrideMode={order.status === 'CANCELLED'}
          onClose={() => setApproveOpen(false)}
        />
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  accent,
  sub,
  action,
}: {
  label: string
  value: string
  accent?: string
  sub?: string
  action?: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
        <span>{label}</span>
        {action}
      </div>
      <div className={`mt-1 text-lg font-semibold ${accent ?? 'text-gray-900'}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-amber-600">{sub}</div>}
    </div>
  )
}
